import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import MaInput from "../../../../Components/UI/ma-input";
import MaButton from "../../../../Components/UI/ma-button";
import GoogleAddressAutocomplete from "../../../../Components/Common/google-address-autocomplete";
import { SettingsSectionCard } from "./settings-section-card";
import { IoIosInformationCircleOutline } from "react-icons/io";
import MaSwitch from "../../../../Components/UI/ma-switch";
import { UPDATE_STORE } from "../../../../Utils/Mutations";
import { GET_CATEGORIES_N_SUB } from "../../../../Utils/Queries";
import { useProfile } from "../../../../Utils/ProfileContext";
import { uploadFileToCloudinary } from "../../../../Utils/api";
import {
  StoreBannerUpload,
  StoreLogoUpload,
  useStoreBrandingFiles,
} from "./settings-store-branding";
import {
  getStoreCoordinates,
  normalizeMediaUrl,
} from "../../../utils/get-profile";

const EASE = [0.22, 1, 0.36, 1];

const emptyToNull = (value) => {
  const trimmed = value?.trim?.() ?? value;
  return trimmed || null;
};

const getItemId = (item) => {
  if (!item) return null;
  if (typeof item === "string") return item;
  return item._id ?? item.id ?? item.category?._id ?? null;
};

const normalizeIdList = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(getItemId).filter(Boolean);
};

function SaveBtn({ onClick, disabled, loading }) {
  return (
    <MaButton
      variant="primary"
      title="Save Changes"
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      sx={{
        bgcolor: "#2E1834",
        minHeight: "44px",
        px: 3,
        borderRadius: "12px",
        fontWeight: 600,
      }}
    />
  );
}

async function uploadStoreImage(file) {
  const response = await uploadFileToCloudinary(file);
  return response?.data?.secure_url ?? null;
}

export function SettingsStorePanel({ profile: profileProp }) {
  const { enqueueSnackbar } = useSnackbar();
  const { refetchProfile } = useProfile();
  const storeFromRedux = useSelector((s) => s.user.profile?.store);
  const store = profileProp?.store || storeFromRedux;

  const [shop, setShop] = useState({
    name: "",
    description: "",
    address: "",
    latitude: null,
    longitude: null,
    registeredNumber: "",
    employerIdentificationNumber: "",
    categoriesToSell: [],
  });
  const [storeLogo, setStoreLogo] = useState(null);
  const [storeBannerUrl, setStoreBannerUrl] = useState(null);
  const [profilePublic, setProfilePublic] = useState(true);
  const [saveError, setSaveError] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const branding = useStoreBrandingFiles({
    onError: (msg) => enqueueSnackbar(msg, { variant: "warning" }),
  });

  const [updateStore, { loading: saving }] = useMutation(UPDATE_STORE);
  const { data: categoriesData, loading: categoriesLoading } =
    useQuery(GET_CATEGORIES_N_SUB);

  const categoryOptions = useMemo(() => {
    const rows = categoriesData?.fetchAllCategoriesAndTheirSubCategories;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row) => row?.category)
      .filter((category) => category?._id && category.isActive !== false)
      .map((category) => ({
        value: category._id,
        label: category.name,
      }));
  }, [categoriesData]);

  const selectedCategoryOptions = useMemo(() => {
    const selected = new Set(shop.categoriesToSell);
    return categoryOptions.filter((option) => selected.has(option.value));
  }, [categoryOptions, shop.categoriesToSell]);

  useEffect(() => {
    if (!store) return;

    const { latitude, longitude } = getStoreCoordinates(profileProp);

    setShop({
      name: store.storeName || "",
      description: store.storeDescription || "",
      address: store.address || "",
      latitude,
      longitude,
      registeredNumber: store.registeredNumber || "",
      employerIdentificationNumber:
        store.employerIdentificationNumber || "",
      categoriesToSell: normalizeIdList(store.categoriesToSell),
    });
    setStoreLogo(normalizeMediaUrl(store.storeLogo));
    setStoreBannerUrl(normalizeMediaUrl(store.storeBannerUrl));
    branding.syncFromStore(store);
    setProfilePublic(store.isStoreActive ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, profileProp]);

  const set = (key) => (e) =>
    setShop((st) => ({ ...st, [key]: e.target.value }));

  const handleCategoriesChange = (_, selectedOptions) => {
    setShop((st) => ({
      ...st,
      categoriesToSell: selectedOptions.map((option) => option.value),
    }));
  };

  const handleAddressChange = ({ address, latitude, longitude }) => {
    setShop((st) => ({
      ...st,
      address: address ?? "",
      ...(latitude != null ? { latitude } : {}),
      ...(longitude != null ? { longitude } : {}),
    }));
  };

  const storeInitial = useMemo(() => {
    return (store?.storeName?.[0] || "S").toUpperCase();
  }, [store]);

  const buildVariables = (overrides = {}) => ({
    storeName: shop.name.trim() || null,
    registeredNumber: emptyToNull(shop.registeredNumber),
    employerIdentificationNumber: emptyToNull(shop.employerIdentificationNumber),
    address: shop.address.trim() || null,
    storeLogo: storeLogo || null,
    storeBannerUrl: storeBannerUrl || null,
    storeDescription: emptyToNull(shop.description),
    categoriesToSell: shop.categoriesToSell,
    ...overrides,
  });

  const resolveBrandingUrls = async () => {
    let nextLogo = storeLogo;
    let nextBanner = storeBannerUrl;

    if (branding.logoFile) {
      const url = await uploadStoreImage(branding.logoFile);
      if (!url) throw new Error("Logo upload failed.");
      nextLogo = url;
    }

    if (branding.bannerFile) {
      const url = await uploadStoreImage(branding.bannerFile);
      if (!url) throw new Error("Banner upload failed.");
      nextBanner = url;
    }

    return { storeLogo: nextLogo, storeBannerUrl: nextBanner };
  };

  const persistStore = async (overrides = {}) => {
    await updateStore({ variables: buildVariables(overrides) });
    try {
      await refetchProfile();
    } catch {
      // Non-fatal.
    }
  };

  const handleSaveDetails = async () => {
    if (!shop.name.trim()) {
      setSaveError("Store name is required.");
      return;
    }
    if (!shop.address.trim()) {
      setSaveError("Business address is required.");
      return;
    }

    setSaveError(null);
    const isUploading = branding.hasPendingUpload;

    try {
      if (isUploading) setUploadingImages(true);

      const brandingUrls = isUploading
        ? await resolveBrandingUrls()
        : { storeLogo, storeBannerUrl };

      await persistStore(brandingUrls);

      if (isUploading) {
        setStoreLogo(brandingUrls.storeLogo);
        setStoreBannerUrl(brandingUrls.storeBannerUrl);
        branding.clearPendingFiles();
      }

      enqueueSnackbar("Store details updated.", { variant: "success" });
    } catch (err) {
      setSaveError(err?.message ?? "Could not update store.");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSaveBranding = async () => {
    if (!branding.hasPendingUpload) {
      enqueueSnackbar("Choose a logo or banner to upload.", { variant: "info" });
      return;
    }

    setSaveError(null);
    setUploadingImages(true);

    try {
      const brandingUrls = await resolveBrandingUrls();
      await persistStore(brandingUrls);
      setStoreLogo(brandingUrls.storeLogo);
      setStoreBannerUrl(brandingUrls.storeBannerUrl);
      branding.clearPendingFiles();
      enqueueSnackbar("Store branding updated.", { variant: "success" });
    } catch (err) {
      setSaveError(err?.message ?? "Could not update store branding.");
    } finally {
      setUploadingImages(false);
    }
  };

  const isBusy = saving || uploadingImages;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: EASE }}
      className="flex flex-col gap-8"
    >
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="flex justify-center"
          >
            <span className="text-[#FF465C] text-[13px] font-medium border border-solid w-fit bg-[#FF465C1a] rounded-full border-[#FF465C] px-3 py-1 text-center">
              {saveError}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsSectionCard
        title="Store Details"
        subtitle="This appears on your storefront and listings."
        footer={
          <SaveBtn
            onClick={handleSaveDetails}
            disabled={isBusy}
            loading={saving && !uploadingImages}
          />
        }
      >
        <MaInput
          label="Shop Name"
          fullWidth
          value={shop.name}
          onChange={set("name")}
          borderColor="#E5E7EB"
          disabled={isBusy}
        />
        <MaInput
          label="Shop Description"
          multiline
          minRows={3}
          fullWidth
          noHeight
          value={shop.description}
          onChange={set("description")}
          borderColor="#E5E7EB"
          disabled={isBusy}
        />
        <GoogleAddressAutocomplete
          label="Business Address"
          value={shop.address}
          onAddressChange={handleAddressChange}
          placeholder="Find a location"
          borderColor="#E5E7EB"
          disabled={isBusy}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={categoryOptions}
          value={selectedCategoryOptions}
          loading={categoriesLoading}
          disabled={isBusy || categoriesLoading}
          onChange={handleCategoriesChange}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label ?? ""}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.label}
                  size="small"
                  {...tagProps}
                  sx={{
                    bgcolor: "#F5EEF7",
                    color: "#2E1834",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <div>
              <label className="mb-2 block text-[13px] font-semibold text-[#272727]">
                Categories to Sell
              </label>
              <TextField
                {...params}
                placeholder={
                  selectedCategoryOptions.length
                    ? ""
                    : categoriesLoading
                      ? "Loading categories..."
                      : "Select categories"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "46px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                      borderRadius: "10px",
                    },
                    "&:hover fieldset": { borderColor: "#E5E7EB" },
                    "&.Mui-focused fieldset": { borderColor: "#E5E7EB" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#717171",
                    opacity: 1,
                  },
                }}
              />
            </div>
          )}
        />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE, delay: 0.05 }}
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <MaInput
            label="Registered Number (Optional)"
            fullWidth
            value={shop.registeredNumber}
            onChange={set("registeredNumber")}
            borderColor="#E5E7EB"
            disabled={isBusy}
          />
          <MaInput
            label="Employer Identification Number (Optional)"
            fullWidth
            value={shop.employerIdentificationNumber}
            onChange={set("employerIdentificationNumber")}
            borderColor="#E5E7EB"
            disabled={isBusy}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE, delay: 0.1 }}
          className="flex items-center gap-x-2"
        >
          <IoIosInformationCircleOutline />
          <span className="text-[12px] text-[#626262]">
            Shown on your public seller profile
          </span>
        </motion.div>
      </SettingsSectionCard>

      <SettingsSectionCard
        title="Store Banner & Logo"
        subtitle="Your store banner is shown at the top of your public page"
        footer={
          <SaveBtn
            onClick={handleSaveBranding}
            disabled={isBusy}
            loading={uploadingImages || (saving && branding.hasPendingUpload)}
          />
        }
      >
        <StoreLogoUpload
          previewUrl={branding.logoPreview}
          fallbackLetter={storeInitial}
          disabled={isBusy}
          uploading={uploadingImages && Boolean(branding.logoFile)}
          onFileSelect={branding.selectLogo}
        />

        <StoreBannerUpload
          previewUrl={branding.bannerPreview}
          disabled={isBusy}
          uploading={uploadingImages && Boolean(branding.bannerFile)}
          onFileSelect={branding.selectBanner}
        />
      </SettingsSectionCard>

      <SettingsSectionCard
        title="Store Visibility"
        subtitle="Control whether your store is publicly accessible"
      >
        <div className="flex flex-col gap-3 rounded-xl border border-[#F3F4F6] bg-[#FAFAFA] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <p className="my-0 text-[13px] font-semibold text-[#111827]">
              Public shop profile
            </p>
            <p className="my-0 mt-1 text-[12px] text-[#6B7280]">
              When off, your storefront link is hidden from browse pages.
            </p>
          </motion.div>
          <MaSwitch
            checked={profilePublic}
            onChange={(_, v) => setProfilePublic(v)}
            disabled
          />
        </div>
        <p className="my-0 mt-2 text-[12px] text-[#9CA3AF]">
          Visibility updates are not available in this API yet.
        </p>
      </SettingsSectionCard>
    </motion.div>
  );
}
